/* eslint-disable react/prop-types */
import flowapayErrorCodes from './flowapay-error-codes.json';
import {
  AlertTriangle,
  Ban,
  BookOpen,
  CheckCircle2,
  CreditCard,
  History,
  KeyRound,
  ShieldAlert,
  ShieldCheck,
  Webhook,
} from 'lucide-react';

import {
  CodeBlock,
  SectionHeader,
  EndpointList,
} from './DocumentationPrimitives';

export const flowapayEndpointDefaults = {
  auth: 'Profile-bound Bearer API key',
  surface: 'U-Compat client API',
  version: 'v1 (/api/v1)',
  includeInLeftNav: true,
};

export const flowapaySections = [
  {
    id: 'fp-overview',
    title: 'Overview',
    icon: BookOpen,
    summary: 'What the U-Compat surface is, who it is for, and how a profile gets switched onto it.',
  },
  {
    id: 'fp-authentication',
    title: 'Authentication',
    icon: KeyRound,
    summary: 'Profile-bound Bearer keys, optional IP allowlists, and how cross-profile access behaves.',
  },
  {
    id: 'fp-payments',
    title: 'Payments',
    icon: CreditCard,
    summary: 'Create deposits and refunds, retrieve any object by ID, and list with filters. There is no separate /refunds route.',
    endpoints: [
      {
        id: 'fp-create-deposit',
        title: 'Create deposit',
        method: 'POST',
        path: '/api/v1/payments',
        description:
          'Create a customer payment. Omit card for hosted checkout (redirectUrl). Send a complete card object for server-to-server card (PCI required); the response may be COMPLETED immediately or CHECKOUT with a redirectUrl when the connector still needs customer action.',
        requestFields: [
          ['paymentType', 'string', 'Required. DEPOSIT for a customer payment. WITHDRAWAL is not available on this surface and returns a 3.17 error.'],
          ['currency', 'string', 'Required. ISO 4217 currency code, for example USD.'],
          ['amount', 'number', 'Required. JSON number in major units, for example 65.40 — not a string. Must match the currency exponent exactly — 65.401 for USD is rejected as excess precision. Maximum 1000000000 major units. Null, zero, and negative are rejected.'],
          ['referenceId', 'string', 'Optional. Your reference for the payment; queryable via referenceId.eq on the list endpoint.'],
          ['description', 'string', 'Optional free-text description.'],
          ['paymentMethod', 'string', 'Optional payment channel (for example BASIC_CARD). Omit it to let the customer choose on the hosted page when card is also omitted. An unrecognised value returns a 3.17 error with field paymentMethod.'],
          ['card', 'object', 'Optional. Server-to-server card: cardNumber, cardSecurityCode, expiryMonth (2 digits), expiryYear (4 digits), cardholderName. Requires PCI DSS compliance on your side. cardToken is not accepted. Omit card entirely for hosted checkout.'],
          ['returnUrl', 'string', 'Optional. Where the customer is sent after a hosted or redirect flow completes.'],
          ['webhookUrl', 'string', 'Optional. Per-payment callback URL. When set, webhooks for this payment go here instead of the profile webhook URL. Immutable — it cannot be changed after creation.'],
          ['customer', 'object', 'Optional. referenceId, citizenshipCountryCode, firstName, lastName, dateOfBirth, email, phone, locale, ip. firstName/lastName are forwarded as billing names when the processor needs them. An invalid email returns 3.30; a phone value with no digits returns 3.21.'],
          ['billingAddress', 'object', 'Optional. addressLine1, addressLine2, city, countryCode, postalCode, state.'],
          ['additionalParameters', 'map<string,string>', 'Optional string-to-string map echoed back on the payment object. Must not contain card-number-shaped values. On S2S card, acceptLanguage, colorDepth, screenHeight, screenWidth, and timeZone are optional device hints — the collect page overwrites them with values from the customer browser.'],
          ['checkoutStyle', 'string', 'Optional hosted-checkout styling hint (hosted path only).'],
        ],
        responseFields: [
          ['result.id', 'string', 'Payment ID — exactly 32 lowercase hex characters. Store it.'],
          ['result.created', 'timestamp', 'Object timestamp, seconds precision, no UTC offset (2026-08-06T10:11:12).'],
          ['result.paymentType', 'string', 'DEPOSIT.'],
          ['result.state', 'string', 'CHECKOUT while customer action is still required; COMPLETED / DECLINED / other terminal states when the processor settles immediately.'],
          ['result.amount', 'number', 'The total charged for the payment — the order amount plus any appended transaction fees — formatted to the currency exponent. Not a verbatim echo of the request amount; when appended fees apply it is larger (for example request 65.40, response 66.90). Same value on create, retrieve, list rows, and the webhook body.'],
          ['result.currency', 'string', 'ISO 4217 code.'],
          ['result.redirectUrl', 'string', 'Present when the customer must continue on a hosted page, phone verification / browser-info collect page, or connector challenge. Omit handling when absent (for example an immediate COMPLETED S2S result).'],
        ],
        notes: [
          'Optional response fields are omitted entirely when absent, never sent as null.',
          'Hosted path: omit card (and optionally paymentMethod). Expect state CHECKOUT and a redirectUrl — send the customer there.',
          'S2S path: send a complete card object. You must be PCI DSS compliant to collect card data. Incomplete card fields return 3.02; cardToken returns 3.02 on field cardToken.',
          'S2S card on processors that need browser/device fields (for example SolidPayments Direct) returns CHECKOUT with a redirectUrl even when there is no 3DS challenge. Send the customer there. A complete additionalParameters device set does not skip that redirect; collected values replace any merchant-sent hints.',
          'Phone verification: when it is enabled on your profile, every customer-present deposit (S2S card and hosted checkout alike) returns CHECKOUT with a redirectUrl. That page first asks the customer for a one-time code sent by SMS, then continues to the processor. Your integration does not change. The verified number is stored on the customer and reused on later payments, so send a stable per-customer customer.referenceId; without one every payment creates a new customer and the customer is asked again. customer.phone is never trusted for this step. Too many wrong codes decline the payment with 6.00.',
          'Open redirectUrl as a top-level navigation (full-page redirect). Do not load it in an iframe or popup: the browser-info step itself renders anywhere, but the bank 3DS challenge and the final return to your returnUrl may refuse to run inside a frame.',
          'googlePay and applePay objects are still rejected as raw credentials (3.02).',
          'preAuth: true, startRecurring: true, recurringToken, and subscription are all rejected as unavailable capabilities.',
          'Omitting amount is not supported on this surface (error field amount) — always send it.',
        ],
      },
      {
        id: 'fp-create-refund',
        title: 'Create refund',
        method: 'POST',
        path: '/api/v1/payments',
        description: 'Refund a completed deposit. A refund is a payment of type REFUND — there is no separate /refunds route on this surface.',
        requestFields: [
          ['paymentType', 'string', 'Required. REFUND.'],
          ['parentPaymentId', 'string', 'Required. The 32-hex ID of the deposit being refunded.'],
          ['currency', 'string', 'Required. ISO 4217 currency code of the refund.'],
          ['amount', 'number', 'Required. JSON number in major units, same parsing rules as deposits.'],
          ['referenceId', 'string', 'Optional. Your reference for the refund.'],
          ['description', 'string', 'Optional free-text description.'],
          ['webhookUrl', 'string', 'Optional per-refund callback URL, preferred over the profile webhook URL. Immutable after creation.'],
        ],
        responseFields: [
          ['result.id', 'string', 'Refund payment ID — 32 lowercase hex characters, distinct from the parent.'],
          ['result.paymentType', 'string', 'REFUND.'],
          ['result.parentPaymentId', 'string', 'The deposit this refund applies to.'],
          ['result.state', 'string', 'Refund lifecycle state (PENDING until processed).'],
        ],
        notes: [
          'If you are migrating code that called POST /refunds on your previous gateway, point it at POST /payments with paymentType REFUND instead.',
          'Retrieve a refund the same way as any payment: GET /api/v1/payments/{id}.',
        ],
      },
      {
        id: 'fp-retrieve-payment',
        title: 'Retrieve payment',
        method: 'GET',
        path: '/api/v1/payments/{id}',
        description: 'Retrieve any object — deposit, refund, or chargeback — by its 32-hex ID. One route serves all three.',
        requestFields: [['id', 'path string', 'Required. 32-lowercase-hex object ID from a create response or webhook.']],
        responseFields: [
          ['result.id', 'string', 'Object ID.'],
          ['result.paymentType', 'string', 'DEPOSIT, REFUND, or CHARGEBACK. CHARGEBACK appears only in responses, never on create.'],
          ['result.state', 'string', 'Current state — see the Webhooks section for the state list.'],
          ['result.amount', 'number', 'The total charged for the payment — the order amount plus any appended transaction fees — formatted to the currency exponent. Not a verbatim echo of the request amount; when appended fees apply it is larger (for example request 65.40, response 66.90). Same value on create, retrieve, list rows, and the webhook body.'],
          ['result.errorCode', 'string', 'Vendor-format error code when the payment failed (see Errors).'],
          ['result.errorMessage', 'string', 'Human-readable companion to errorCode.'],
          ['result.externalId', 'string', 'Processor-side reference when available.'],
          ['result.additionalParameters', 'map<string,string>', 'Echoed from create.'],
        ],
        notes: [
          'An ID that belongs to another profile returns 404 — it is indistinguishable from an ID that does not exist. Do not treat 404 as proof the object was never created; check which key you are using first.',
        ],
      },
      {
        id: 'fp-list-payments',
        title: 'List payments',
        method: 'GET',
        path: '/api/v1/payments',
        description: 'List payments newest-first with time-window and reference filters. Rows are a narrower shape than the single-object result.',
        requestFields: [
          ['created.gte', 'query timestamp', 'Optional lower created bound. If no created filter is sent, the window defaults to the last 24 hours.'],
          ['created.lt', 'query timestamp', 'Optional upper created bound.'],
          ['updated.gte', 'query timestamp', 'Optional lower updated bound.'],
          ['updated.lt', 'query timestamp', 'Optional upper updated bound.'],
          ['referenceId.eq', 'query string', 'Optional exact match on your referenceId.'],
          ['limit', 'query integer', 'Optional. 1–1000, default 50.'],
          ['offset', 'query integer', 'Optional. 0–1000000, default 0.'],
        ],
        responseFields: [
          ['hasMore', 'boolean', 'True when more rows exist beyond this page — increment offset to fetch them.'],
          ['result', 'array', 'Payment rows, newest first with the payment ID as a stable tiebreak.'],
          ['result[].amount', 'number', 'The total charged for the payment — the order amount plus any appended transaction fees — formatted to the currency exponent. Not a verbatim echo of the request amount; when appended fees apply it is larger (for example request 65.40, response 66.90). Same value on create, retrieve, list rows, and the webhook body.'],
        ],
        notes: [
          'List rows never carry externalId, additionalParameters, or externalRefs — retrieve the single object when you need those.',
          'Without created filters you only see the last 24 hours. Reconciliation jobs must send explicit created.gte / created.lt bounds.',
        ],
      },
    ],
  },
  {
    id: 'fp-chargebacks',
    title: 'Chargebacks',
    icon: ShieldAlert,
    summary: 'Register externally-received chargebacks against a payment so both sides agree on the record.',
    endpoints: [
      {
        id: 'fp-register-chargeback',
        title: 'Register chargeback',
        method: 'POST',
        path: '/api/v1/payments/{id}/chargebacks',
        description: 'Record a chargeback against a deposit. The chargeback becomes its own payment object of type CHARGEBACK.',
        requestFields: [
          ['description', 'string', 'Optional, up to 512 characters.'],
          ['created', 'timestamp', 'Optional backdated object timestamp (YYYY-MM-DDTHH:MM:SS). Omit to use now.'],
          ['state', 'string', 'Optional initial state, constrained to CANCELLED, DECLINED, or COMPLETED. Omitted means the chargeback opens as PENDING.'],
          ['webhookUrl', 'string', 'Optional per-chargeback callback URL, up to 512 characters. Immutable after creation.'],
        ],
        responseFields: [
          ['result.id', 'string', 'Minted chargeback ID — 32 lowercase hex characters.'],
          ['result.paymentType', 'string', 'CHARGEBACK.'],
          ['result.parentPaymentId', 'string', 'The deposit the chargeback applies to.'],
          ['result.state', 'string', 'The state you supplied, or PENDING when omitted.'],
        ],
        notes: [
          'Registering a chargeback does not create a refund and does not move money — it records the dispute.',
          'A payment can carry at most one chargeback. Registering a second returns error code 3.29.',
        ],
      },
    ],
  },
  {
    id: 'fp-operations',
    title: 'Operations',
    icon: History,
    summary: 'Per-payment processing timeline: which steps ran and what state each left the payment in.',
    endpoints: [
      {
        id: 'fp-list-operations',
        title: 'List operations',
        method: 'GET',
        path: '/api/v1/payments/{id}/operations',
        description: 'List the processing steps recorded for one payment, in sequence order.',
        requestFields: [['id', 'path string', 'Required. 32-lowercase-hex payment ID.']],
        responseFields: [
          ['result[].id', 'integer', 'Per-payment sequence number starting at 1. It is not globally unique — two different payments both have an operation 1.'],
          ['result[].operation', 'string', 'Step name, for example CREATE_PAYMENT, CHECKOUT, CONFIRMATION, REDIRECT.'],
          ['result[].started', 'timestamp', 'Step start, envelope format with milliseconds and offset.'],
          ['result[].completed', 'timestamp | null', 'Step end; null while the step is still running.'],
          ['result[].paymentState', 'string', 'Payment state after the step.'],
        ],
        notes: [
          'Operations are metadata only — there are no request or response message bodies attached.',
        ],
      },
    ],
  },
  {
    id: 'fp-webhooks',
    title: 'Webhooks',
    icon: Webhook,
    summary: 'HMAC-SHA256-signed callbacks whose body is the payment object itself — a different contract from native webhooks.',
  },
  {
    id: 'fp-errors',
    title: 'Errors',
    icon: AlertTriangle,
    summary: 'The 4xx error body shape, and the two separate channels error codes arrive through.',
  },
  {
    id: 'fp-unsupported',
    title: 'Unsupported capabilities',
    icon: Ban,
    summary: 'Wire-complete fields/routes that return 3.17, plus source-gateway OpenAPI paths that are not registered here (404). Migrating merchants will hit these.',
  },
];

export function buildFlowapayExamples(baseUrl) {
  const api = `${baseUrl}/api/v1`;
  const auth = "--header 'Authorization: Bearer <YOUR_API_KEY>'";

  return {
    'fp-create-deposit': {
      request: `# Hosted checkout (omit card) — customer enters details on redirectUrl
curl --location '${api}/payments' \\
  --header 'Content-Type: application/json' \\
  ${auth} \\
  --data-raw '{
    "paymentType": "DEPOSIT",
    "currency": "USD",
    "amount": 65.40,
    "referenceId": "order-1001",
    "description": "Order #1001",
    "returnUrl": "https://example.com/checkout/complete",
    "webhookUrl": "https://example.com/webhooks/vexgate",
    "customer": {
      "referenceId": "customer-123",
      "email": "jane@example.com"
    }
  }'

# Server-to-server card (PCI DSS required) — may return COMPLETED or CHECKOUT + redirectUrl
curl --location '${api}/payments' \\
  --header 'Content-Type: application/json' \\
  ${auth} \\
  --data-raw '{
    "paymentType": "DEPOSIT",
    "currency": "USD",
    "amount": 65.40,
    "paymentMethod": "BASIC_CARD",
    "referenceId": "order-1002",
    "customer": {
      "firstName": "Jane",
      "lastName": "Doe",
      "email": "jane@example.com"
    },
    "card": {
      "cardNumber": "4111111111111111",
      "cardSecurityCode": "123",
      "expiryMonth": "01",
      "expiryYear": "2030",
      "cardholderName": "Jane Doe"
    }
  }'`,
      response: `{
  "timestamp": "2026-08-06T10:11:12.123+00:00",
  "status": 200,
  "result": {
    "id": "9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e",
    "referenceId": "order-1001",
    "created": "2026-08-06T10:11:12",
    "paymentType": "DEPOSIT",
    "state": "CHECKOUT",
    "description": "Order #1001",
    "amount": 65.40,
    "currency": "USD",
    "redirectUrl": "https://checkout.example/session/…"
  }
}`,
    },
    'fp-create-refund': {
      request: `curl --location '${api}/payments' \\
  --header 'Content-Type: application/json' \\
  ${auth} \\
  --data-raw '{
    "paymentType": "REFUND",
    "parentPaymentId": "9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e",
    "currency": "USD",
    "amount": 15.00,
    "referenceId": "refund-1001"
  }'`,
      response: `{
  "timestamp": "2026-08-06T11:02:33.456+00:00",
  "status": 200,
  "result": {
    "id": "4a7d2c9f1e8b3a6d5c0f7e2b9a4d8c1f",
    "referenceId": "refund-1001",
    "created": "2026-08-06T11:02:33",
    "paymentType": "REFUND",
    "parentPaymentId": "9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e",
    "state": "PENDING",
    "amount": 15.00,
    "currency": "USD"
  }
}`,
    },
    'fp-retrieve-payment': {
      request: `curl --location '${api}/payments/9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e' \\
  ${auth}`,
      response: `{
  "timestamp": "2026-08-06T12:00:05.789+00:00",
  "status": 200,
  "result": {
    "id": "9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e",
    "referenceId": "order-1001",
    "created": "2026-08-06T10:11:12",
    "paymentType": "DEPOSIT",
    "state": "COMPLETED",
    "amount": 65.40,
    "currency": "USD",
    "additionalParameters": {
      "campaign": "summer"
    }
  }
}`,
    },
    'fp-list-payments': {
      request: `curl --location '${api}/payments?created.gte=2026-08-01T00:00:00&created.lt=2026-08-07T00:00:00&limit=50&offset=0' \\
  ${auth}`,
      response: `{
  "timestamp": "2026-08-06T12:30:00.123+00:00",
  "status": 200,
  "hasMore": false,
  "result": [
    {
      "id": "4a7d2c9f1e8b3a6d5c0f7e2b9a4d8c1f",
      "referenceId": "refund-1001",
      "created": "2026-08-06T11:02:33",
      "paymentType": "REFUND",
      "parentPaymentId": "9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e",
      "state": "COMPLETED",
      "amount": 15.00,
      "currency": "USD"
    },
    {
      "id": "9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e",
      "referenceId": "order-1001",
      "created": "2026-08-06T10:11:12",
      "paymentType": "DEPOSIT",
      "state": "COMPLETED",
      "amount": 65.40,
      "currency": "USD"
    }
  ]
}`,
    },
    'fp-register-chargeback': {
      request: `curl --location '${api}/payments/9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e/chargebacks' \\
  --header 'Content-Type: application/json' \\
  ${auth} \\
  --data-raw '{
    "description": "Issuer chargeback, reason 10.4",
    "created": "2026-08-05T09:00:00",
    "state": "COMPLETED"
  }'`,
      response: `{
  "timestamp": "2026-08-06T13:00:00.321+00:00",
  "status": 200,
  "result": {
    "id": "c1e8b3a6d5c0f7e2b9a4d8c1f4a7d2c9",
    "created": "2026-08-05T09:00:00",
    "paymentType": "CHARGEBACK",
    "parentPaymentId": "9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e",
    "state": "COMPLETED",
    "description": "Issuer chargeback, reason 10.4"
  }
}`,
    },
    'fp-list-operations': {
      request: `curl --location '${api}/payments/9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e/operations' \\
  ${auth}`,
      response: `{
  "timestamp": "2026-08-06T12:45:00.654+00:00",
  "status": 200,
  "result": [
    {
      "id": 1,
      "operation": "CREATE_PAYMENT",
      "started": "2026-08-06T10:11:12.100+00:00",
      "completed": "2026-08-06T10:11:12.180+00:00",
      "paymentState": "CHECKOUT"
    },
    {
      "id": 2,
      "operation": "CONFIRMATION",
      "started": "2026-08-06T10:14:02.000+00:00",
      "completed": "2026-08-06T10:14:03.250+00:00",
      "paymentState": "COMPLETED"
    }
  ]
}`,
    },
  };
}

const FLOWAPAY_STATES = [
  ['CHECKOUT', 'Customer action still required — hosted checkout, phone verification / browser-info collect page, or connector redirect/challenge.', 'No'],
  ['PENDING', 'Processing, or an open chargeback.', 'No'],
  ['AUTHORIZED', 'Funds authorized, not yet captured.', 'Yes'],
  ['COMPLETED', 'Funds captured / refund processed.', 'Yes'],
  ['DECLINED', 'Declined by acquirer or issuer.', 'Yes'],
  ['CANCELLED', 'Cancelled, including timeout (errorCode 1.04).', 'Yes'],
  ['AWAITING_APPROVAL', 'Wire-compatible only — will not occur on this surface.', 'No'],
];

const HTTP_ERROR_CODES = [
  ['1.00', 'Illegal Workflow State', 'The operation is not valid for the payment’s current state.'],
  ['1.03', 'Internal Server Error', 'Integrity conflict detected while handling the request.'],
  ['1.08', 'Invalid Amount', 'Zero, negative, null, non-numeric, overflow, or excess precision for the currency.'],
  ['1.09', 'Invalid Currency', 'Currency is not a supported ISO 4217 code.'],
  ['3.02', 'Invalid request format or missing required parameters', 'Malformed body, query, or path — including a missing amount. The message carries the specific detail after a colon.'],
  ['3.17', 'Channel not active', 'Every unavailable capability; the message reads "Channel not active: <capability> is unavailable". See Unsupported capabilities.'],
  ['3.21', 'Invalid phone number', 'customer.phone is present but contains no digits. Compact digit-only values are accepted.'],
  ['3.28', 'Amount higher than maximum', 'Amount exceeds 1000000000 major units.'],
  ['3.29', 'Transaction has chargeback already', 'Second chargeback registration on the same payment.'],
  ['3.30', 'Invalid Email', 'customer.email is not a valid email address.'],
];

// The catalog itself comes from vex-router (`pnpm sync:error-codes` copies
// docs/flowapay-api-compat/flowapay-error-codes.json, generated from
// common_enums::flowapay_catalog::FLOWAPAY_CATALOG). The prose columns below
// are ours; codes without a note fall back to the family of their VEXGATE code.
const OBJECT_ERROR_SOURCES = {
  '1.00': 'Unexpected payment state, or processor passthrough',
  '1.01': 'Connector lookup miss on force-sync (not a VEXGATE 404 for a missing payment)',
  '1.02': 'Connector communication problem',
  '1.03': 'Integrity conflict — state stays PENDING; no webhook is sent',
  '1.04': 'Session or processor timeout, or collect-page session expiry — state collapses to CANCELLED',
  '1.05': 'Processor passthrough',
  '1.09': 'Currency or corridor not enabled at the processor',
  '2.00': 'Customer abandoned or cancelled',
  '3.00': 'Acquirer decline, including historic PayZero card_details',
  '3.01': 'Processor / acquirer fraud engine',
  '3.02': 'Invalid request to the processor',
  '3.03': 'Downstream technical issue, including historic PayZero temporary / technical / processing_error',
  '3.04': 'Acquirer or processor timeout (distinct from customer 1.04)',
  '3.05': 'Processor or acquirer rate limit',
  '3.06': 'Card not supported / restricted',
  '3.07': 'Payment-method data rejected',
  '3.08': 'Compliance or sanctions restriction',
  '3.09': 'Authentication required',
  '3.10': 'Unmapped processor failure — the catch-all',
  '3.16': 'Duplicate request',
  '3.17': 'Capability, connector, or routing not available',
  '3.18': 'Customer could not complete a hosted step (for example JavaScript disabled on the collect page); also PayZero cancelled rows',
  '3.29': 'Processor passthrough',
  '3.31': 'Issuer unavailable',
  '4.00': 'Generic issuer decline when a finer issuer code is not stored',
  '4.01': 'Issuer: insufficient funds',
  '4.02': 'Issuer: do not honor',
  '4.03': 'Account closed or invalid',
  '4.04': 'Invalid expiry',
  '4.05': 'Credit or velocity limit',
  '4.06': 'Lost or stolen card',
  '4.07': 'Invalid CVV or authentication code',
  '4.09': 'Issuer anti-fraud (not VEXGATE internal FRM)',
  '4.10': 'Operation not allowed',
  '4.11': 'Address mismatch',
  '4.13': 'Invalid PAN',
  '5.00': 'Customer failed the 3DS challenge',
  '5.02': '3DS service, configuration, or protocol error',
};

// VEXGATE meanings come from `vex_message` in the generated catalog
// (`StandardisedCode::message()`); several variants can share a family code,
// so distinct messages are joined.
const VEX_MEANINGS = {};
for (const { vex_code: vexCode, vex_message: vexMessage } of flowapayErrorCodes.codes) {
  if (!vexMessage) continue;
  const known = VEX_MEANINGS[vexCode];
  if (!known) {
    VEX_MEANINGS[vexCode] = vexMessage;
  } else if (!known.split(' / ').includes(vexMessage)) {
    VEX_MEANINGS[vexCode] = `${known} / ${vexMessage}`;
  }
}

const VEX_FAMILY_MEANINGS = {
  IS: 'Issuer decline',
  AE: 'Acquirer / processor decline',
  TD: '3DS authentication',
  CE: 'Connector or technical',
  HE: 'Platform',
  IR: 'Request or configuration',
  AF: 'VEXGATE anti-fraud',
  XF: 'External anti-fraud',
  MR: 'Merchant decision',
};

const vexFamilyMeaning = (vexCode) =>
  VEX_FAMILY_MEANINGS[vexCode.slice(0, 2)] ?? 'Processor passthrough';

const OBJECT_ERROR_CODES = flowapayErrorCodes.codes.map(
  ({ code, message, vex_code: vexCode }) => [
    code,
    message,
    OBJECT_ERROR_SOURCES[code] ?? vexFamilyMeaning(vexCode),
  ],
);

const vexToFlowapay = new Map();
for (const { code, message, vex_code: vexCode } of flowapayErrorCodes.codes) {
  if (!vexToFlowapay.has(vexCode)) {
    vexToFlowapay.set(vexCode, `${code} ${message}`);
  }
}
const VEX_TO_FLOWAPAY = [...vexToFlowapay.entries()].map(([vexCode, compat]) => [
  vexCode,
  VEX_MEANINGS[vexCode] ?? vexFamilyMeaning(vexCode),
  compat,
]);

const UNSUPPORTED_CAPABILITIES = [
  ['Pre-authorization', 'preAuth', 'preAuth: true on create'],
  ['Capture', 'capture', 'POST /payments/{id}/capture without amount'],
  ['Partial capture', 'amount', 'POST /payments/{id}/capture with amount'],
  ['Void', 'void', 'POST /payments/{id}/void'],
  ['Recurring payment', 'startRecurring / recurringToken', 'Those fields on create'],
  ['Subscription', 'subscription', 'That field on create'],
  ['Withdrawal', 'paymentType', 'paymentType: "WITHDRAWAL" on create'],
  ['Checkout without an amount', 'amount', 'amount omitted on create'],
  ['Payment channel', 'paymentMethod', 'Unrecognised paymentMethod value'],
  ['Payment approval', 'approve', 'POST /payments/{id}/approve'],
  ['Payment rejection', 'reject', 'POST /payments/{id}/reject'],
];

/** Source-gateway OpenAPI routes that are not registered on this surface (framework 404, not typed 3.17). */
const UNSUPPORTED_ROUTES = [
  ['Execute deposit (skip hosted checkout)', 'PATCH', '/api/v1/payments/{id}'],
  ['List shop balances', 'GET', '/api/v1/balances'],
  ['Available withdrawal balances', 'GET', '/api/v1/available-withdrawal-balances'],
  ['Get subscription', 'GET', '/api/v1/subscriptions/{id}'],
  ['Patch subscription', 'PATCH', '/api/v1/subscriptions/{id}'],
  ['Customer card tokens', 'GET', '/api/v1/customers/{customer.referenceId}/card-tokens'],
  ['Customer terminal tokens', 'GET', '/api/v1/customers/{customerReferenceId}/terminals/{terminalId}/tokens'],
];

function FlowapayOverviewSection({ baseUrl, onSelectSection }) {
  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950 p-6 text-white sm:p-8">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-300/10 px-3 py-1 text-xs font-medium text-cyan-100">
            <BookOpen className="h-3.5 w-3.5" />
            U-Compat client API
          </div>
          <h2 className="max-w-3xl text-3xl font-semibold tracking-tight sm:text-4xl">
            Keep your existing integration. Point it at VEXGATE.
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
            This surface accepts U-Compat-shaped requests and returns U-Compat-shaped responses
            and webhooks, so a merchant migrating from a gateway that speaks that contract changes
            the base URL and API key, not their integration code. Deposits support hosted checkout (omit{' '}
            <code className="rounded bg-white/10 px-1.5 py-0.5 text-xs">card</code>) or server-to-server
            card when you are PCI DSS compliant. Refunds, chargeback registration, and the operations
            timeline are live; capabilities this surface still withholds return well-formed vendor
            errors instead.
          </p>
        </div>
        <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Base URL</p>
            <code className="mt-2 block break-all text-xs text-slate-900">{baseUrl}/api/v1</code>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Auth header</p>
            <code className="mt-2 block break-all text-xs text-slate-900">Authorization: Bearer &lt;YOUR_API_KEY&gt;</code>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
        <div className="flex gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
          <div>
            <h2 className="text-sm font-semibold text-amber-950">One profile, one contract.</h2>
            <p className="mt-1 text-sm leading-6 text-amber-900">
              A VEXGATE profile speaks either the native contract or the U-Compat
              contract — never both. The switch is made by VEXGATE staff on request; there is no
              setting for it in this panel, so if you are unsure which contract your profile is on,
              ask us before integrating. Until the profile is switched, webhooks arrive in the
              native format and will fail your U-Compat SHA256 signature verification — a
              silent mismatch, not an error you will see in a response.
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={CheckCircle2}
          title="Contract facts worth knowing up front"
          description="These are the details that differ from other gateways and from VEXGATE's native API."
        />
        <div className="grid gap-3 md:grid-cols-2">
          {[
            'IDs are exactly 32 lowercase hex characters — payments, refunds, and chargebacks alike.',
            'Two timestamp formats by design: object created is seconds with no offset (2026-08-06T10:11:12); envelope timestamp carries milliseconds and +00:00.',
            'Amounts are JSON numbers in major units (65.40), not strings. Parsed exactly against the currency exponent (no IEEE rounding of the token); responses are formatted back to that exponent (JPY 6540).',
            'Optional response fields are omitted, not null.',
            'Refunds are POST /payments with paymentType REFUND — there is no /refunds route.',
            'Webhook bodies are the payment object itself, signed with HMAC-SHA256 in the Signature header — not the native event envelope.',
          ].map((item) => (
            <div key={item} className="flex gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
              <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-cyan-700" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={BookOpen}
          title="Start here"
          description="The two pages most integrations need first."
        />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            ['fp-authentication', 'Authentication', 'Bearer keys, profile binding, IP allowlists.'],
            ['fp-payments', 'Payments', 'Deposits, refunds, retrieve, and list.'],
            ['fp-webhooks', 'Webhooks', 'SHA256 signatures and delivery rules.'],
            ['fp-unsupported', 'Unsupported', 'What returns 3.17 instead of working.'],
          ].map(([id, title, description]) => (
            <button
              key={id}
              type="button"
              onClick={() => onSelectSection(id)}
              className="rounded-xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-cyan-300 hover:bg-cyan-50"
            >
              <p className="text-sm font-semibold text-slate-950">{title}</p>
              <p className="mt-1 text-xs leading-5 text-slate-600">{description}</p>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function FlowapayAuthenticationSection() {
  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={KeyRound}
          title="Authentication"
          description="Every request carries a Bearer API key that is bound to exactly one merchant and one profile."
        />
        <div className="space-y-3 text-sm leading-6 text-slate-700">
          <p>
            Send <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-900">Authorization: Bearer &lt;YOUR_API_KEY&gt;</code> on
            every request. The scheme match is case-insensitive, so <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-900">bearer</code> works
            too. The native <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-900">api-key</code> header
            does <strong>not</strong> authenticate this surface.
          </p>
          <p>
            The key resolves to one profile. You never send a profile ID in request bodies — the key
            carries it. A consequence worth designing for: requesting an object that belongs to a
            different profile returns <strong>404</strong>, indistinguishable from an ID that does
            not exist. If a retrieve unexpectedly 404s, check which key you used before concluding
            the object is gone.
          </p>
          <p>
            API keys are issued and managed by VEXGATE staff. To create, rotate, or revoke a key for
            this surface, contact us — keys for the U-Compat contract are not self-service in this panel.
          </p>
        </div>
        <div className="mt-4">
          <CodeBlock
            title="request-headers.txt"
            code={`Content-Type: application/json
Authorization: Bearer <YOUR_API_KEY>`}
          />
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={ShieldCheck}
          title="Optional IP allowlist"
          description="A key can be restricted to caller IPs. Ask us to set or change the list."
        />
        <div className="space-y-3 text-sm leading-6 text-slate-700">
          <p>
            Allowlists accept IPv4 and IPv6 addresses and CIDR ranges. Entries are canonicalised on
            save: host bits are cleared (<code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">192.0.2.129/24</code> becomes{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">192.0.2.0/24</code>,{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">2001:db8::1234/64</code> becomes{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">2001:db8::/64</code>), and a{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">/32</code> collapses to the bare
            address. If the list you get back looks different from what you sent, that is the
            canonical form, not a data error.
          </p>
          <p>
            Requests from an address outside the allowlist are rejected before any payment logic runs.
          </p>
        </div>
      </section>
    </div>
  );
}

function FlowapayWebhooksSection() {
  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={Webhook}
          title="Webhooks"
          description="The webhook body is the payment object itself — the same shape as a retrieve — signed with HMAC-SHA256."
        />
        <div className="space-y-3 text-sm leading-6 text-slate-700">
          <p>
            There is no event envelope on this surface: no <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">event_id</code>,
            no <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">type</code>, no{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">content.object</code> nesting. The POST body is the
            full payment result — identical in shape to <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">GET /api/v1/payments/{'{id}'}</code>.
            Read the object <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">id</code> and{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">state</code> to decide what changed.{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">amount</code> is the charged total
            including any appended transaction fees, identical to retrieve.
          </p>
          <p>
            Delivery targets the payment&apos;s own <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">webhookUrl</code> when
            one was supplied at create time, and the profile webhook URL otherwise. The per-object
            URL is <strong>immutable</strong>: there is no API to change it after creation. If you
            supply a per-payment URL that later goes dead, deliveries for that payment are lost —
            reconcile it by polling retrieve. Prefer the profile URL unless you genuinely need
            per-payment routing, and treat any URL you do send as permanent.
          </p>
        </div>
        <div className="mt-4">
          <CodeBlock
            title="example-delivery.http"
            code={`POST /webhooks/vexgate HTTP/1.1
Host: example.com
Content-Type: application/json
Signature: 7f3a2b1c9d8e0f4a6b5c3d2e1f0a9b8c7d6e5f4a3b2c1d0e9f8a7b6c5d4e3f2a

{
  "id": "9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e",
  "referenceId": "order-1001",
  "created": "2026-08-06T10:11:12",
  "paymentType": "DEPOSIT",
  "state": "COMPLETED",
  "amount": 65.40,
  "currency": "USD"
}`}
          />
          <p className="mt-2 text-xs leading-5 text-slate-500">
            The body is exactly the <code className="rounded bg-slate-100 px-1 py-0.5">result</code> object
            you would get from retrieve — same fields, same omit-when-absent rule — without the{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5">timestamp</code>/<code className="rounded bg-slate-100 px-1 py-0.5">status</code> envelope.
            A refund delivery additionally carries <code className="rounded bg-slate-100 px-1 py-0.5">parentPaymentId</code>;
            a chargeback carries <code className="rounded bg-slate-100 px-1 py-0.5">paymentType: &quot;CHARGEBACK&quot;</code>.
          </p>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={ShieldCheck}
          title="Signature verification"
          description="HMAC-SHA256, hex-encoded, over the raw request body, in the Signature header."
        />
        <div className="space-y-3 text-sm leading-6 text-slate-700">
          <p>
            Compute HMAC-SHA256 of the raw body with your profile signing key and compare it to
            the <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-900">Signature</code> header.
            This is deliberately different from native webhooks
            (<code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">X-Webhook-Signature-512</code>, SHA512) — if your
            verifier sees SHA512-style signatures, your profile is still on the native contract.
            Delivery is fail-closed: if no signing key is configured for the profile, nothing is sent.
          </p>
        </div>
        <div className="mt-4">
          <CodeBlock
            title="verify-u-compat-webhook.js"
            code={`const crypto = require('crypto');

function verifyWebhook(rawBody, signatureHeader, signingKey) {
  const expected = crypto
    .createHmac('sha256', signingKey)
    .update(rawBody, 'utf8')
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected, 'hex'),
    Buffer.from(signatureHeader, 'hex')
  );
}

// In your webhook handler:
// const signature = req.headers['signature'];
// if (!verifyWebhook(req.rawBody, signature, SIGNING_KEY)) {
//   return res.status(401).json({ error: 'Invalid signature' });
// }`}
          />
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={History}
          title="Which states are delivered"
          description="Only terminal-ish states produce a webhook. Poll or rely on the redirect for the rest."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="pb-2 pr-4">State</th>
                <th className="pb-2 pr-4">Meaning</th>
                <th className="pb-2">Webhook</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {FLOWAPAY_STATES.map(([state, meaning, delivered]) => (
                <tr key={state}>
                  <td className="py-2 pr-4"><code className="text-xs font-semibold">{state}</code></td>
                  <td className="py-2 pr-4">{meaning}</td>
                  <td className="py-2">{delivered}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm leading-6 text-slate-600">
          One exception on top of the table: a payment that ends in an integrity conflict
          (errorCode 1.03, state PENDING) emits no webhook at all. Reconcile those by polling
          the retrieve endpoint.
        </p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={CheckCircle2}
          title="Deduplication and retries"
          description="Idempotency comes from the object, not an event ID."
        />
        <div className="space-y-3 text-sm leading-6 text-slate-700">
          <p>
            Delivery is deduplicated on the combination of payment, object type, and public state —
            the same payment reaching the same state twice produces one logical delivery, even under
            concurrent schedulers. Retries are also stale-suppressed: a pending retry re-checks the
            payment&apos;s current state and is dropped if the payment has moved on, so
            an <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">AUTHORIZED</code> webhook
            cannot arrive after you have already received <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">COMPLETED</code>.
          </p>
          <p>
            For your own idempotency, key processing on <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">id</code> +{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">state</code> of the delivered object.
            Your endpoint must return 2xx to acknowledge; anything else is retried.
          </p>
        </div>
      </section>
    </div>
  );
}

function FlowapayErrorsSection() {
  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={AlertTriangle}
          title="Errors"
          description="Error codes arrive through two different channels. Handle both, and do not mix them up."
        />
        <div className="space-y-3 text-sm leading-6 text-slate-700">
          <p>
            <strong>Channel A — HTTP error body.</strong> Request-level failures return a 4xx with
            the body below; the code is <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">errors[0].codes[0]</code> and
            the <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">field</code> tells you which input tripped it.
          </p>
          <p>
            <strong>Channel B — errorCode / errorMessage on the payment object.</strong> Processing
            failures do not produce an HTTP error at all: the payment object you retrieve, list, or
            receive by webhook carries <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">errorCode</code> and{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">errorMessage</code> alongside a{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">DECLINED</code> or{' '}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">CANCELLED</code> state.
          </p>
        </div>
        <div className="mt-4">
          <CodeBlock
            title="bad-request-body.json"
            code={`{
  "timestamp": "2026-08-06T10:11:12.123+00:00",
  "status": 400,
  "error": "Bad Request",
  "message": "Capture is unavailable",
  "errors": [
    {
      "codes": ["3.17"],
      "arguments": [],
      "defaultMessage": "Capture is unavailable",
      "objectName": "payment",
      "field": "capture",
      "bindingFailure": false
    }
  ],
  "path": "/api/v1/payments/9f3b6c1e8a2d4f0b7c5e1a9d3b8f6c2e/capture"
}`}
          />
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={AlertTriangle}
          title="Channel A codes (HTTP error body)"
          description="Returned as errors[].codes[0] on 4xx responses."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="pb-2 pr-4">Code</th>
                <th className="pb-2 pr-4">Meaning</th>
                <th className="pb-2">Raised when</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {HTTP_ERROR_CODES.map(([code, meaning, when]) => (
                <tr key={code}>
                  <td className="py-2 pr-4"><code className="text-xs font-semibold">{code}</code></td>
                  <td className="py-2 pr-4">{meaning}</td>
                  <td className="py-2">{when}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm leading-6 text-slate-600">
          Authentication failures return the vendor string <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">&quot;Invalid token length&quot;</code>;
          internal errors return <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">&quot;The request could not be processed&quot;</code>.
          A payment that does not exist in VEXGATE is HTTP 404 without an <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">errors[]</code> array
          — that is not Channel A code <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">1.01</code>.
        </p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={AlertTriangle}
          title="Channel B codes (on the payment object)"
          description="Appear as errorCode / errorMessage on retrieved, listed, or webhook-delivered objects."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="pb-2 pr-4">Code</th>
                <th className="pb-2 pr-4">Message</th>
                <th className="pb-2">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {OBJECT_ERROR_CODES.map(([code, meaning, source]) => (
                <tr key={code}>
                  <td className="py-2 pr-4"><code className="text-xs font-semibold">{code}</code></td>
                  <td className="py-2 pr-4">{meaning}</td>
                  <td className="py-2">{source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm leading-6 text-slate-600">
          This is the set this surface can emit — not a closed catalog. Unmapped processor
          failures become <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">3.10</code>.
          Codes the original U-Compat catalog can produce but this surface does not raise
          (for example <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">5.01</code>,{' '}
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">6.xx</code>) are omitted on purpose.
          Two codes also change state mechanics:{' '}
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">1.04</code> means
          the payment timed out and collapsed to CANCELLED, and{' '}
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">1.03</code> (message literally
          &quot;Internal Server Error&quot;) means an integrity conflict — the payment stays PENDING
          and <strong>no webhook is sent</strong>. Treat{' '}
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">3.10</code> as &quot;unmapped
          processor failure&quot;, not as a code to branch on.
        </p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={AlertTriangle}
          title="How VEXGATE families translate"
          description="Internal family codes never appear on this surface. They are listed so a retrieve / list / webhook errorCode can be read back to the VEXGATE classification."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="pb-2 pr-4">VEXGATE</th>
                <th className="pb-2 pr-4">Meaning</th>
                <th className="pb-2">This surface</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {VEX_TO_FLOWAPAY.map(([vex, meaning, compat]) => (
                <tr key={vex}>
                  <td className="py-2 pr-4"><code className="text-xs font-semibold">{vex}</code></td>
                  <td className="py-2 pr-4">{meaning}</td>
                  <td className="py-2">{compat}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function FlowapayUnsupportedSection() {
  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={Ban}
          title="Unsupported capabilities"
          description="These calls and fields are wire-complete but deliberately unavailable. They return a well-formed 3.17 error, not a 404 — branch on the field value to tell them apart."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="pb-2 pr-4">Capability</th>
                <th className="pb-2 pr-4">error field</th>
                <th className="pb-2">Triggered by</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {UNSUPPORTED_CAPABILITIES.map(([capability, field, trigger]) => (
                <tr key={capability}>
                  <td className="py-2 pr-4 font-medium">{capability}</td>
                  <td className="py-2 pr-4"><code className="text-xs font-semibold">{field}</code></td>
                  <td className="py-2">{trigger}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm leading-6 text-slate-600">
          All of these return HTTP 400 with code <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">3.17</code> and
          a per-capability <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">defaultMessage</code>. Note the capture
          split: capture <em>without</em> an amount reports field <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">capture</code>,
          capture <em>with</em> an amount reports field <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">amount</code> (partial capture) —
          same code, different field and message.
        </p>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Google&nbsp;Pay and Apple&nbsp;Pay objects on create are still rejected as raw credentials
          (HTTP 400 / code <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">3.02</code>),
          not as <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">3.17</code> unavailable
          capabilities. A complete <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">card</code>{' '}
          object is accepted for server-to-server deposits when you are PCI DSS compliant;{' '}
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">cardToken</code> and incomplete
          card fields are rejected with <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">3.02</code>.
          Omit <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">card</code> for hosted checkout.
        </p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader
          icon={Ban}
          title="Unsupported source-gateway routes"
          description="These paths exist in the source gateway's OpenAPI but are not registered on this surface. They return HTTP 404 — not a typed 3.17 body."
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="pb-2 pr-4">Capability</th>
                <th className="pb-2 pr-4">Method</th>
                <th className="pb-2">Path</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {UNSUPPORTED_ROUTES.map(([capability, method, path]) => (
                <tr key={`${method} ${path}`}>
                  <td className="py-2 pr-4 font-medium">{capability}</td>
                  <td className="py-2 pr-4"><code className="text-xs font-semibold">{method}</code></td>
                  <td className="py-2"><code className="text-xs">{path}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm leading-6 text-slate-600">
          Do not treat these 404s as &quot;payment not found&quot; — the route itself is absent.
          Wire-complete stubs that return <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">3.17</code>{' '}
          (capture, void, approve, reject) are listed in the table above; migrate off these missing
          routes rather than probing them.
        </p>
      </section>
    </div>
  );
}

function FlowapaySectionOverview({ section, endpoints, onSelectEndpoint }) {
  const Icon = section.icon;

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionHeader icon={Icon} title={section.title} description={section.summary} />
        <div className="rounded-xl border border-cyan-100 bg-cyan-50 p-4 text-sm leading-6 text-cyan-950">
          These endpoints belong to the U-Compat client API at /api/v1 and require a
          profile-bound Bearer API key on a profile switched to the U-Compat contract. They are not
          interchangeable with the native v1 endpoints.
        </div>
      </section>
      <EndpointList endpoints={endpoints} onSelectEndpoint={onSelectEndpoint} />
    </div>
  );
}

export function FlowapaySectionContent({ section, endpoints, baseUrl, onSelectSection, onSelectEndpoint }) {
  if (section.id === 'fp-overview') {
    return <FlowapayOverviewSection baseUrl={baseUrl} onSelectSection={onSelectSection} />;
  }
  if (section.id === 'fp-authentication') {
    return <FlowapayAuthenticationSection />;
  }
  if (section.id === 'fp-webhooks') {
    return <FlowapayWebhooksSection />;
  }
  if (section.id === 'fp-errors') {
    return <FlowapayErrorsSection />;
  }
  if (section.id === 'fp-unsupported') {
    return <FlowapayUnsupportedSection />;
  }
  return (
    <FlowapaySectionOverview
      section={section}
      endpoints={endpoints}
      onSelectEndpoint={onSelectEndpoint}
    />
  );
}
